import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RefreshCw, Settings, Download } from 'lucide-react';
import PageSkeleton from '../common/Skeleton';
import {
  Button,
  DataTable,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  EmptyState,
  AlertMessage,
  PageHeader,
  SearchFilterBar,
  Modal
} from '../common/ui';
import { useOrders } from '../../hooks/useOrders';
import { formatDateTime } from '../../utils/dateUtils';
import { formatOrderSyncMessage, getAssignmentStatus } from '../../utils/orderUtils';
import { SyncProgressBar } from './SyncProgressBar';
import { SyncStatusIndicator } from './SyncStatusIndicator';
import { deleteOrder, setOrdersHidden } from '../../services/ordersApi';
import { fetchOrderStatuses } from '../../services/b2bSyncApi';
import { getUserPreference, setUserPreference } from '../../hooks/useUserPreferences';

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [success, setSuccess] = useState('');
  // An order sync that finished with problems. It stays until the user closes it.
  const [warning, setWarning] = useState('');
  const [searchTerm, setSearchTerm] = useState(() => {
    // Initialize from URL query params if present
    return searchParams.get('search') || '';
  });
  const [filter, setFilter] = useState<'all' | 'pending' | 'complete'>(() => {
    const filterFromUrl = searchParams.get('filter');
    if (filterFromUrl === 'all' || filterFromUrl === 'pending' || filterFromUrl === 'complete') {
      return filterFromUrl;
    }
    const saved = getUserPreference('orders', 'filter', 'pending');
    if (saved === 'all' || saved === 'pending' || saved === 'complete') {
      return saved as 'all' | 'pending' | 'complete';
    }
    return 'pending';
  });
  const [showHidden, setShowHidden] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<number>>(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [bulkDeleteModal, setBulkDeleteModal] = useState<{ show: boolean; count: number }>({ show: false, count: 0 });
  const [bulkOperationLoading, setBulkOperationLoading] = useState(false);
  const [statusLabels, setStatusLabels] = useState<Record<string, string>>({});
  const [importModal, setImportModal] = useState(false);
  const [importOrderNumber, setImportOrderNumber] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  
  const {
    orders,
    loading,
    error,
    syncing,
    syncStatus,
    statusCounts,
    autoSyncDone,
    handleSyncOrders,
    handleSyncSingleOrder,
    handleSearch: triggerSearch,
    fetchOrders,
    refreshStatusCounts
  } = useOrders({ filter, search: searchTerm, autoSync: true, includeHidden: filter === 'pending' && showHidden });

  const handleSync = async () => {
    console.debug('[Orders] handleSync: clicked');
    const result = await handleSyncOrders();
    console.debug('[Orders] handleSync: result', result);
    setWarning('');
    if (result && result.success) {
      const { message, warning: hasProblems } = formatOrderSyncMessage(result, 'Orders: ');
      if (hasProblems) {
        setSuccess('');
        setWarning(message);
      } else {
        setSuccess(message);
        setTimeout(() => setSuccess(''), 5000);
      }
    } else {
      setSuccess('');
    }
  };

  const handleImportOrder = async () => {
    if (!importOrderNumber.trim()) return;
    setImportLoading(true);
    try {
      const result = await handleSyncSingleOrder(importOrderNumber.trim());
      if (result?.success && result.order) {
        setSuccess(`Imported order #${result.order.order_number} (${result.order.items_synced} items)`);
        setImportModal(false);
        setImportOrderNumber('');
      } else {
        setSuccess('');
        // Error is set by the hook via setError
      }
    } finally {
      setImportLoading(false);
    }
    setTimeout(() => setSuccess(''), 5000);
  };

  useEffect(() => {
    console.debug('[Orders] data render', {
      count: orders.length,
      filter,
      searchTerm,
      syncing
    });
  }, [orders, filter, searchTerm, syncing]);

  const handleSearch = () => {
    // Update URL params
    updateUrlParams(searchTerm, filter);
    triggerSearch(searchTerm);
  };

  const handleStatusFilterChange = (status: 'all' | 'pending' | 'complete') => {
    setFilter(status);
    setUserPreference('orders', 'filter', status);
    if (status !== 'pending') setShowHidden(false);
    // Update URL params
    updateUrlParams(searchTerm, status);
  };

  // Helper function to update URL parameters
  const updateUrlParams = (search: string, filterValue: 'all' | 'pending' | 'complete') => {
    const newParams = new URLSearchParams();
    
    // Only add non-default values to keep URLs clean
    if (search && search.trim()) {
      newParams.set('search', search.trim());
    }
    if (filterValue && filterValue !== 'pending') {
      newParams.set('filter', filterValue);
    }
    
    setSearchParams(newParams);
  };

  // Listen to URL parameter changes (for browser back/forward)
  useEffect(() => {
    const searchFromUrl = searchParams.get('search') || '';
    const filterFromUrl = searchParams.get('filter');
    const defaultFilter = getUserPreference('orders', 'filter', 'pending');
    const validFilter: 'all' | 'pending' | 'complete' = 
      (filterFromUrl === 'all' || filterFromUrl === 'pending' || filterFromUrl === 'complete') 
        ? filterFromUrl 
        : (defaultFilter === 'all' || defaultFilter === 'complete') ? defaultFilter as 'all' | 'complete' : 'pending';
    
    // Only update if values actually changed
    if (searchFromUrl !== searchTerm || validFilter !== filter) {
      setSearchTerm(searchFromUrl);
      setFilter(validFilter);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear selections when filter or search changes
  useEffect(() => {
    setSelectedOrderIds(new Set());
    setLastSelectedIndex(null);
  }, [filter, searchTerm]);

  // Fetch status labels on mount
  useEffect(() => {
    const loadStatusLabels = async () => {
      try {
        const response = await fetchOrderStatuses();
        if (response.success && response.mapping) {
          setStatusLabels(response.mapping);
        }
      } catch (err) {
        console.error('Error fetching status labels:', err);
      }
    };
    loadStatusLabels();
  }, []);

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(orders.map(o => o.id));
      setSelectedOrderIds(allIds);
    } else {
      setSelectedOrderIds(new Set());
    }
    setLastSelectedIndex(null);
  };

  const handleOrderSelect = (orderId: number, index: number, e: React.MouseEvent | React.ChangeEvent) => {
    if ('stopPropagation' in e) {
      e.stopPropagation();
    }
    const newSelection = new Set(selectedOrderIds);

    const clickEvent = e as React.MouseEvent;
    if (clickEvent.shiftKey && lastSelectedIndex !== null) {
      // Range selection with Shift+Click
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      const rangeOrders = orders.slice(start, end + 1);
      rangeOrders.forEach(o => newSelection.add(o.id));
    } else if (clickEvent.ctrlKey || clickEvent.metaKey) {
      // Toggle individual selection with Ctrl/Cmd+Click
      if (newSelection.has(orderId)) {
        newSelection.delete(orderId);
      } else {
        newSelection.add(orderId);
      }
    } else {
      // Simple click - toggle single item
      if (newSelection.has(orderId)) {
        newSelection.delete(orderId);
      } else {
        newSelection.add(orderId);
      }
    }

    setSelectedOrderIds(newSelection);
    setLastSelectedIndex(index);
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedOrderIds.size === 0) return;

    setBulkOperationLoading(true);
    setSuccess('');

    const idsToDelete = Array.from(selectedOrderIds);
    let successCount = 0;
    let failCount = 0;
    const errors: string[] = [];

    for (const orderId of idsToDelete) {
      try {
        await deleteOrder(orderId);
        successCount++;
      } catch (err: any) {
        failCount++;
        const order = orders.find(o => o.id === orderId);
        const orderNumber = order ? `#${order.order_number}` : `Order ${orderId}`;
        errors.push(orderNumber);
        console.error(`Error deleting order ${orderId}:`, err);
      }
    }

    // Refresh orders list and status counts
    await Promise.all([fetchOrders(), refreshStatusCounts()]);

    // Clear selections
    setSelectedOrderIds(new Set());
    setLastSelectedIndex(null);
    setBulkDeleteModal({ show: false, count: 0 });
    setBulkOperationLoading(false);

    // Show success/error message
    if (failCount === 0) {
      setSuccess(`Successfully deleted ${successCount} order${successCount > 1 ? 's' : ''}`);
    } else {
      setSuccess(`Deleted ${successCount} order${successCount > 1 ? 's' : ''}, failed to delete ${failCount}: ${errors.join(', ')}`);
    }
    setTimeout(() => {
      setSuccess('');
    }, 5000);
  };

  const handleSetHidden = async (hidden: boolean) => {
    if (selectedOrderIds.size === 0) return;
    setBulkOperationLoading(true);
    setSuccess('');

    try {
      const ids = Array.from(selectedOrderIds);
      const result = await setOrdersHidden(ids, hidden);
      await Promise.all([fetchOrders(), refreshStatusCounts()]);
      setSelectedOrderIds(new Set());
      setLastSelectedIndex(null);
      const label = hidden ? 'hidden' : 'unhidden';
      setSuccess(`Successfully ${label} ${result.count} order${result.count > 1 ? 's' : ''}`);
    } catch (err: any) {
      console.error('Error setting orders hidden:', err);
      setSuccess('');
    } finally {
      setBulkOperationLoading(false);
      setTimeout(() => setSuccess(''), 5000);
    }
  };

  // Calculate select all state
  const allSelected = orders.length > 0 && orders.every(o => selectedOrderIds.has(o.id));
  const someSelected = orders.some(o => selectedOrderIds.has(o.id));
  const isIndeterminate = someSelected && !allSelected;

  // Don't show skeleton if auto-sync is in progress (show the sync progress bar instead)
  const showInitialSkeleton = loading && !autoSyncDone && !syncing;

  if (showInitialSkeleton) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Orders</h1>
        <p className="text-sm text-gray-500 mb-4">Loading...</p>
        <PageSkeleton rows={5} rowHeight="h-20" />
      </div>
    );
  }

  return (
    <div className="p-6 pb-20">
      <PageHeader
        title="Orders"
        subtitle="View and manage B2B orders"
        reserveWidthPx={280}
        actions={
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => navigate('/orders/settings')}
              icon={Settings}
            >
              Product Mapping
            </Button>
            <Button
              variant="secondary"
              onClick={() => setImportModal(true)}
              icon={Download}
            >
              Import Order
            </Button>
            <Button
              variant="primary"
              onClick={handleSync}
              disabled={syncing}
              icon={RefreshCw}
            >
              Sync Orders
            </Button>
          </div>
        }
        onBack={undefined}
      />

      <SyncProgressBar 
        isSyncing={syncing} 
        message="Syncing orders from B2B..." 
        progress={
          syncing && syncStatus?.metadata?.current && syncStatus?.metadata?.total
            ? {
                current: syncStatus.metadata.current,
                total: syncStatus.metadata.total
              }
            : undefined
        }
      />
      <SyncStatusIndicator syncStatus={syncStatus} />

      {/* Search and Filter Bar */}
      <div className="mb-4">
        <SearchFilterBar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          onSearchSubmit={handleSearch}
          searchPlaceholder="Search by order number, customer, or serial..."
          onSearch={handleSearch}
          onFilterChange={() => {}}
        />
      </div>

      {/* Status Filter Buttons */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => handleStatusFilterChange('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-naviter-blue text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
            <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
              filter === 'all' ? 'bg-white/20' : 'bg-gray-200'
            }`}>
              {statusCounts.all || 0}
            </span>
          </button>
          
          <button
            onClick={() => handleStatusFilterChange('pending')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'pending'
                ? 'bg-yellow-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Waiting for S/Ns
            <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
              filter === 'pending' ? 'bg-white/20' : 'bg-gray-200'
            }`}>
              {statusCounts.pending || 0}
            </span>
          </button>
          
          <button
            onClick={() => handleStatusFilterChange('complete')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'complete'
                ? 'bg-green-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Completed
            <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
              filter === 'complete' ? 'bg-white/20' : 'bg-gray-200'
            }`}>
              {statusCounts.complete || 0}
            </span>
          </button>

          {filter === 'pending' && (statusCounts.hidden || 0) > 0 && (
            <label className="inline-flex items-center gap-2 ml-auto cursor-pointer select-none">
              <span className="text-sm text-gray-500">Show hidden ({statusCounts.hidden})</span>
              <button
                type="button"
                role="switch"
                aria-checked={showHidden}
                onClick={() => setShowHidden(!showHidden)}
                className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 ${
                  showHidden ? 'bg-gray-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
                    showHidden ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </button>
            </label>
          )}
        </div>
      </div>

      {/* Success/Error Messages */}
      {error && <AlertMessage type="error" message={error} title="" onDismiss={() => {}} dismissible>{''}</AlertMessage>}
      {success && <AlertMessage type="success" message={success} title="" onDismiss={() => setSuccess('')} dismissible>{''}</AlertMessage>}
      {warning && <AlertMessage type="warning" message={warning} title="" className="whitespace-pre-line" onDismiss={() => setWarning('')} dismissible>{''}</AlertMessage>}

      {/* Bulk Actions Bar */}
      {!loading && selectedOrderIds.size > 0 && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-md p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-blue-900">
              {selectedOrderIds.size} order{selectedOrderIds.size > 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            {filter === 'pending' && showHidden && orders.some(o => selectedOrderIds.has(o.id) && o.hidden) && (
              <Button
                variant="secondary"
                onClick={() => handleSetHidden(false)}
                disabled={bulkOperationLoading || selectedOrderIds.size === 0}
                icon={undefined}
              >
                Unhide
              </Button>
            )}
            {filter === 'pending' && orders.some(o => selectedOrderIds.has(o.id) && !o.hidden) && (
              <Button
                variant="secondary"
                onClick={() => handleSetHidden(true)}
                disabled={bulkOperationLoading || selectedOrderIds.size === 0}
                icon={undefined}
              >
                Hide
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => {
                const selectedCount = selectedOrderIds.size;
                setBulkDeleteModal({ show: true, count: selectedCount });
              }}
              disabled={bulkOperationLoading || selectedOrderIds.size === 0}
              icon={undefined}
            >
              Delete
            </Button>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className="hidden md:block">
        <DataTable
          emptyState={orders.length === 0 ? (
            <EmptyState
              message={
                searchTerm
                  ? 'No orders match your search'
                  : filter === 'pending'
                    ? 'All orders are fully assigned!'
                    : filter === 'all'
                      ? 'No eligible orders found. Map products in Order Settings, then sync again.'
                      : 'No orders found.'
              }
              action={!searchTerm && filter === 'all'}
              actionText={!searchTerm && filter === 'all' ? 'Sync Orders' : ''}
              onActionClick={!searchTerm && filter === 'all' ? handleSync : () => {}}
            />
          ) : null}
        >
          <colgroup>
            <col style={{ width: '50px' }} />
            <col />
            <col />
            <col />
            <col />
            <col />
          </colgroup>
          <TableHeader>
            <tr>
              <TableHeaderCell responsive={undefined} align="center">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = isIndeterminate;
                  }}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out cursor-pointer"
                  title="Select all orders"
                />
              </TableHeaderCell>
              <TableHeaderCell responsive={undefined}>Order #</TableHeaderCell>
              <TableHeaderCell responsive={undefined}>Customer</TableHeaderCell>
              <TableHeaderCell responsive={undefined}>Date</TableHeaderCell>
              <TableHeaderCell responsive={undefined}>Items</TableHeaderCell>
              <TableHeaderCell responsive={undefined}>Assigned</TableHeaderCell>
            </tr>
          </TableHeader>
          <TableBody>
            {orders.map((order, index) => {
              const assignmentStatus = getAssignmentStatus(order);
              const isSelected = selectedOrderIds.has(order.id);
              
              return (
                <TableRow
                  key={order.id}
                  clickable={true}
                  onClick={() => navigate(`/orders/${order.id}/serials`)}
                  href={`/orders/${order.id}/serials`}
                  onKeyDown={() => {}}
                >
                  <TableCell responsive={undefined} align="center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOrderSelect(order.id, index, e);
                      }}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleOrderSelect(order.id, index, e);
                      }}
                      className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out cursor-pointer"
                      title="Select order"
                    />
                  </TableCell>
                  <TableCell responsive={undefined}>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <a
                          href={`https://b2b.naviter.com/wp-admin/post.php?post=${order.order_number}&action=edit`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-naviter-blue hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          #{order.order_number}
                        </a>
                        {order.hidden && (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-200 text-gray-600 rounded">
                            Hidden
                          </span>
                        )}
                      </div>
                      {order.status && (
                        <div className={`text-xs mt-0.5 ${
                          order.status === 'cancelled' || order.status === 'failed'
                            ? 'text-red-600'
                            : 'text-gray-500'
                        }`}>
                          {statusLabels[order.status] || order.status}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell responsive={undefined}>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{order.customer_name}</div>
                      <div className="text-xs text-gray-500">{order.customer_email}</div>
                    </div>
                  </TableCell>
                  <TableCell responsive={undefined}>
                    <span className="text-sm text-gray-600">{formatDateTime(order.order_date)}</span>
                  </TableCell>
                  <TableCell responsive={undefined}>
                    <span className="text-sm text-gray-900">{order.total_quantity} items</span>
                  </TableCell>
                  <TableCell responsive={undefined}>
                    <span className={`text-sm font-medium ${assignmentStatus.className}`}>
                      {assignmentStatus.text}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </DataTable>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden">
        {orders.length === 0 ? (
          <EmptyState
            message={
              searchTerm
                ? 'No orders match your search'
                : filter === 'all'
                  ? 'No eligible orders found. Map products in Order Settings, then sync again.'
                  : 'No orders found.'
            }
            action={!searchTerm}
            actionText={!searchTerm ? 'Sync Orders' : ''}
            onActionClick={!searchTerm ? handleSync : () => {}}
          />
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              const assignmentStatus = getAssignmentStatus(order);
              
              return (
                <div
                  key={order.id}
                  className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/orders/${order.id}/serials`)}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-1.5">
                        <a
                          href={`https://b2b.naviter.com/wp-admin/post.php?post=${order.order_number}&action=edit`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-naviter-blue hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          #{order.order_number}
                        </a>
                        {order.hidden && (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-200 text-gray-600 rounded">
                            Hidden
                          </span>
                        )}
                      </div>
                      {order.status && (
                        <div className={`text-xs mt-0.5 ${
                          order.status === 'cancelled' || order.status === 'failed'
                            ? 'text-red-600'
                            : 'text-gray-500'
                        }`}>
                          {statusLabels[order.status] || order.status}
                        </div>
                      )}
                      <div className="text-sm text-gray-600">{formatDateTime(order.order_date)}</div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{order.customer_name}</div>
                      <div className="text-xs text-gray-500">{order.customer_email}</div>
                    </div>
                    
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{order.total_quantity} items</span>
                      <span className={`font-medium ${assignmentStatus.className}`}>
                        {assignmentStatus.text}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Import Single Order Modal */}
      {importModal && (
        <Modal
          isOpen={importModal}
          onClose={() => { setImportModal(false); setImportOrderNumber(''); }}
          title="Import Order"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Enter the WooCommerce order number to import it directly. This is useful for orders that were missed by the regular sync.
            </p>
            <div>
              <label htmlFor="importOrderNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Order Number
              </label>
              <input
                id="importOrderNumber"
                type="text"
                value={importOrderNumber}
                onChange={(e) => setImportOrderNumber(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !importLoading) handleImportOrder(); }}
                placeholder="e.g. 147993"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-naviter-blue text-sm"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="secondary"
                onClick={() => { setImportModal(false); setImportOrderNumber(''); }}
                disabled={importLoading}
                icon={undefined}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleImportOrder}
                disabled={importLoading || !importOrderNumber.trim()}
                icon={undefined}
              >
                {importLoading ? 'Importing...' : 'Import'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {bulkDeleteModal.show && (
        <Modal
          isOpen={bulkDeleteModal.show}
          onClose={() => setBulkDeleteModal({ show: false, count: 0 })}
          title="Confirm Bulk Delete"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Are you sure you want to delete {bulkDeleteModal.count} order{bulkDeleteModal.count > 1 ? 's' : ''}?
            </p>
            <p className="text-xs text-gray-600">
              This will permanently remove the selected orders from the database. They will reappear the next time you sync orders from B2B.
            </p>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => setBulkDeleteModal({ show: false, count: 0 })}
                disabled={bulkOperationLoading}
                icon={undefined}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleBulkDelete}
                disabled={bulkOperationLoading}
                icon={undefined}
              >
                {bulkOperationLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Orders;

